import React, { useEffect, useState } from "react";
import { Crown, Zap, Briefcase, Sparkles, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const PLAN_COLOR = {
  free: "bg-teal-soft text-white",
  standard: "bg-orange text-white",
  business: "bg-teal-deep text-white",
  enterprise: "bg-amber-500 text-white",
};
const PLAN_ICON = { free: Zap, standard: Crown, business: Briefcase, enterprise: Sparkles };

const PlansTab = () => {
  const { t } = useI18n();
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/users/by-plan", { params: { plan: filter } })
      .then(({ data }) => setUsers(data.items || []))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [filter]);

  const changePlan = async (uid, newPlan) => {
    try {
      await api.patch(`/admin/users/${uid}/plan`, { plan: newPlan });
      setUsers((prev) => prev.map((u) => u.id === uid ? { ...u, plan: newPlan } : u));
    } catch (e) { alert(formatApiError(e)); }
  };

  const filtered = users.filter((u) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (u.email || "").toLowerCase().includes(s) || (u.full_name || "").toLowerCase().includes(s);
  });

  return (
    <div className="mt-6 space-y-5" data-testid="admin-plans-tab">
      {/* Plan overview cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {plans.map((p) => {
          const Icon = PLAN_ICON[p.id] || Zap;
          const count = users.filter((u) => u.plan === p.id).length;
          return (
            <button
              key={p.id}
              onClick={() => setFilter(filter === p.id ? "all" : p.id)}
              className={`bg-white rounded-2xl p-4 border text-left flex items-center gap-3 transition-all ${filter === p.id ? "border-orange shadow-md" : "border-gray-200 hover:border-teal"}`}
              data-testid={`plan-filter-${p.id}`}
            >
              <span className={`w-10 h-10 rounded-full ${PLAN_COLOR[p.id] || "bg-teal"} flex items-center justify-center`}>
                <Icon size={18} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-teal-soft font-bold">{p.label}</div>
                <div className="font-display text-xl text-teal-deep leading-none mt-0.5">{filter === "all" ? "—" : count}</div>
                <div className="text-[10px] text-teal-soft">{p.profiles === 999 ? "∞" : p.profiles} perfiles · {p.products_per_profile === 999 ? "∞" : p.products_per_profile} items</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-teal-soft" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.adminPlans.searchPh} className="field-input pl-11 rounded-full" data-testid="plans-search" />
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="plans-table">
            <thead className="bg-cream border-b border-gray-200">
              <tr className="text-left text-teal">
                <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.adminPlans.thUser}</th>
                <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">Email</th>
                <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.adminPlans.thRole}</th>
                <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">{t.adminPlans.thPlan}</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={4} className="px-4 py-10 text-center text-teal-soft">…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-teal-soft">{t.adminPlans.empty}</td></tr>}
              {filtered.map((u) => {
                const Icon = PLAN_ICON[u.plan] || Zap;
                return (
                  <tr key={u.id} className="border-b border-gray-100 last:border-b-0" data-testid={`plan-user-${u.id}`}>
                    <td className="px-4 py-3 text-teal-deep font-semibold">{u.full_name || "—"}</td>
                    <td className="px-4 py-3 text-teal-soft">{u.email}</td>
                    <td className="px-4 py-3 text-teal-soft">{u.role}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${PLAN_COLOR[u.plan] || "bg-gray-200 text-gray-600"}`}>
                          <Icon size={11} /> {u.plan}
                        </span>
                        <select
                          value={u.plan}
                          onChange={(e) => changePlan(u.id, e.target.value)}
                          className="text-xs rounded-full border border-gray-300 px-2 py-1 bg-white"
                          data-testid={`plan-select-${u.id}`}
                        >
                          {plans.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                        </select>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlansTab;
